import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Subject, catchError, debounceTime, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../components/reset-filter-button/reset-filter-button.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { ArtPillComponent } from '../../components/art-pill/art-pill.component';
import { UmgebungPillComponent } from '../../components/umgebung-pill/umgebung-pill.component';
import { ResPillComponent, ResPillStatus } from '../../components/res-pill/res-pill.component';
import { TEST_RUN_TO_RES_PILL } from '../../shared/test-run-outcome';
import { branchTypeFilterValue, environmentFilterLabel, isBranchEnvironment } from '../../shared/branch-name';
import { formatDurationMs } from '../../shared/duration.util';
import { findRouteParam } from '../../shared/route-params';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { TestRunAbortDialogComponent } from '../../components/test-run-abort-dialog/test-run-abort-dialog.component';
import { ABORTABLE_STATUSES, abortErrorKeyFor } from '../../components/test-run-abort-dialog/test-run-abort';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { SavedViewsMenuComponent, SavedViewOption } from '../../shared/saved-views/saved-views-menu.component';
import { buildSavedViewFiltersJson, parseSavedViewFilters } from '../../shared/saved-views/saved-view-filters';
import { OverviewPaginationComponent } from '../../components/overview-pagination/overview-pagination.component';
import { LiveRunInlineComponent } from '../../components/live-run-inline/live-run-inline.component';
import { LivePipelineService } from '../../services/live-pipeline.service';
import { LiveRunsStore } from '../../services/live-runs.store';
import { LivePipelineEntry, pipelineCounts } from '../../models/live-pipeline.model';
import { TestRunService } from '../../services/test-run.service';
import { SavedViewService } from '../../services/saved-view.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { SavedView } from '../../models/saved-view.model';
import {
  ResultStatus,
  RunKind,
  TEST_RUN_ENVIRONMENTS,
  TestRunListFilter,
  TestRunListItem,
  TestRunStats,
  TestRunStatus,
} from '../../models/test-run.model';
import { Page } from '../../models/page.model';
import { testRunExecutionsLink } from '../../utils/test-run-routes';

const SAVED_VIEW_CONTEXT = 'TEST_EXECUTIONS' as const;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_ROUTE_KEY = 'test-runs';
/**
 * Wert der Option „Abgebrochen" im Status-Dropdown (NEX-1400). Sie filtert nach dem
 * Lebenszyklus (`runStatus`), die uebrigen Optionen nach dem Ergebnis (`status`).
 */
const ABORTED_OPTION = 'ABORTED' as const;
const EMPTY_TEST_RUN_VIEW_FILTERS: Required<TestRunListFilter> = {
  query: '',
  art: '',
  status: '',
  environment: '',
  assignee: '',
  runStatus: '',
};

/**
 * Sentinel fuer einen fehlgeschlagenen Request; haelt den Stream am Leben. Ein
 * `null` wuerde sich hier nicht anbieten, weil eine legitim leere Page (kein
 * Ergebnis, kein Fehler) ebenfalls existiert — Symbol markiert den Fehlerfall
 * disjunkt zum Datenfall.
 */
const LOAD_FAILED = Symbol('loadFailed');
/**
 * Verzoegerung fuer den `reload`-Trigger nach einem Tastenanschlag in der Suche.
 * Vorher feuerten `listPaged` UND `getStats` bei jedem Zeichen; `switchMap` cancelt
 * die veraltete Subscription clientseitig, der Server bekommt den (jetzt doppelten)
 * Request trotzdem. 200 ms bleiben unter der Wahrnehmungsschwelle einer flotten
 * Tipperin und fangen alle zusammenhaengenden Anschlaege eines Wortes ein
 * (NEX-1081-Review MZemelkaST).
 */
const QUERY_DEBOUNCE_MS = 200;
/**
 * Buendelt die Enden mehrerer Pipeline-Laeufe zu einem Neuladen (NEX-1520). Der
 * Beobachter meldet je Takt alle Laeufe, die darin geendet haben, kurz hintereinander;
 * ein Neuladen je Lauf schickte ebenso viele Listen- und Kennzahlen-Aufrufe.
 */
const RUN_END_DEBOUNCE_MS = 500;

/** Der Stand der Schritte-Spalte einer Zeile. */
export interface RunProgress {
  passed: number;
  total: number;
  /**
   * „3/6" — an einer Stelle gebildet, weil die Spalte sie zweimal braucht: sichtbar im
   * Label und als `aria-valuetext` des Balkens. Zwei Formatierungen drifteten
   * auseinander, ohne dass es auffiele — gesehen haette es nur, wer vorlesen laesst.
   */
  label: string;
  percent: number;
  /** Ob der Balken rot laeuft. */
  failed: boolean;
  /** Was der Balken zaehlt — Schritte des Laufs oder Testfaelle der Pipeline. */
  labelKey: string;
}

/**
 * Testlauf-Übersicht (NEX-1034). Filter laufen serverseitig — ein Projekt
 * sammelt beliebig viele Läufe an, clientseitiges Filtern würde die ganze
 * Historie übertragen.
 */
@Component({
  selector: 'app-testlauf-uebersicht',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    MatIcon,
    MatTooltip,
    SearchBarComponent,
    FilterDropdownComponent,
    ResetFilterButtonComponent,
    ProjectNavComponent,
    PageHeaderComponent,
    ArtPillComponent,
    UmgebungPillComponent,
    ResPillComponent,
    AvatarComponent,
    ConfirmDialogComponent,
    TestRunAbortDialogComponent,
    StatGroupComponent,
    SavedViewsMenuComponent,
    OverviewPaginationComponent,
    LiveRunInlineComponent,
  ],
  providers: [LivePipelineService, LiveRunsStore],
  templateUrl: './testlauf-uebersicht.component.html',
  styleUrl: './testlauf-uebersicht.component.scss',
})
export class TestlaufUebersichtComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly testRunService = inject(TestRunService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly pageSizePref = inject(PageSizePreferenceService);
  private readonly liveRunsStore = inject(LiveRunsStore);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId: string;

  readonly runs = signal<TestRunListItem[]>([]);
  readonly totalElements = signal(0);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly pageIndex = signal(0);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  /** Projektweite Zahlen; null, solange keine geladen sind oder der Aufruf scheiterte. */
  private readonly statsData = signal<TestRunStats | null>(null);
  /**
   * Getrennt vom Listen-Fehler: schlaegt nur `/stats` fehl, waeren `?? 0`-Kacheln
   * ununterscheidbar von einer echten Grundmenge Null — genau die Klasse „Zahl sieht
   * aus wie eine Grundmenge, ist keine", die dieser PR beseitigen sollte. Signal
   * treibt einen eigenen Fehlerhinweis und schaltet die Kachelwerte auf „—"
   * (NEX-1081-Review MZemelkaST).
   */
  readonly statsError = signal(false);
  readonly deleteError = signal(false);
  /**
   * Vollstaendige Ansichten inklusive filtersJson; die Menu-Komponente bekommt daraus
   * nur Name und Zusammenfassung. So braucht das Anwenden einer Ansicht keinen
   * zweiten Roundtrip.
   */
  private readonly savedViewData = signal<SavedView[]>([]);
  readonly savedViews = computed<SavedViewOption[]>(() =>
    this.savedViewData().map((v) => ({ name: v.name, summary: v.name })),
  );
  private readonly projectAssignees = signal<{ id: string; name: string }[]>([]);
  readonly pendingDelete = signal<TestRunListItem | null>(null);
  readonly deleting = signal(false);

  /** Der Lauf, dessen Abbruch-Dialog offen ist (NEX-1400). */
  readonly pendingAbort = signal<TestRunListItem | null>(null);
  readonly abortSubmitting = signal(false);
  readonly abortErrorKey = signal<string | null>(null);
  /** Nach einem 409 ist die Zeile veraltet; neu geladen wird erst beim Schliessen des Dialogs. */
  private reloadAfterAbortDialog = false;

  /** Die laufenden Pipeline-Durchlaeufe — sie bekommen ein Band und Live-Zahlen in der Zeile. */
  readonly liveRuns = this.liveRunsStore.running;
  /**
   * Laeufe, deren Ende gemeldet ist, deren Zeile aber noch auf die neu geladene Seite
   * wartet (NEX-1520). Ihr Band ist schon weg und `runStatus` steht noch auf RUNNING —
   * ohne diesen Merker boete die Zeile bis zur Antwort „Lauf abbrechen" an.
   */
  private readonly endedRunIds = signal<ReadonlySet<string>>(new Set());

  readonly query = signal('');
  readonly artFilter = signal<RunKind | ''>('');
  readonly statusFilter = signal<ResultStatus | ''>('');
  readonly runStatusFilter = signal<TestRunStatus | ''>('');
  /** Ein Dropdown fuer beide Filter: aktiv ist hoechstens einer von ihnen. */
  readonly statusDropdownValue = computed(() => this.runStatusFilter() || this.statusFilter());
  readonly environmentFilter = signal('');

  protected readonly isBranchEnvironment = isBranchEnvironment;
  readonly assigneeFilter = signal('');

  readonly savedViewContext = SAVED_VIEW_CONTEXT;

  readonly hasFilters = computed(
    () =>
      !!this.query() ||
      !!this.artFilter() ||
      !!this.statusFilter() ||
      !!this.runStatusFilter() ||
      !!this.environmentFilter() ||
      !!this.assigneeFilter(),
  );

  /**
   * Umgebungen: die vier bekannten Werte plus alles, was in den geladenen Zeilen
   * zusaetzlich vorkommt. Bestandsdaten tragen Freitext-Umgebungen, die sonst
   * unfilterbar waeren; die feste Liste stellt sicher, dass die Auswahl nicht vom
   * gerade geladenen Ausschnitt abhaengt.
   *
   * Pipeline-Laeufe tragen ihren Branch. Der steht nicht als ganzer Name zur
   * Auswahl — jeder Lauf brachte sonst seinen eigenen Eintrag und die Liste
   * waere nach einer Woche unbenutzbar. Stattdessen steht sein Typ da
   * (`feature/`), unter dem alle Branches dieser Art zusammenfallen.
   *
   * Der aktive Wert steht immer mit in der Liste: Eine gespeicherte Ansicht kann
   * einen ganzen Branchnamen tragen oder eine Umgebung, die es in den geladenen
   * Zeilen nicht mehr gibt. Serverseitig filtert das weiter richtig — ohne
   * passenden Eintrag zeigte die Auswahl aber "Alle Umgebungen" ueber einer
   * gefilterten Liste.
   */
  readonly environmentOptions = computed(() => {
    const active = this.environmentFilter();
    return [
      ...new Set([
        ...TEST_RUN_ENVIRONMENTS,
        ...this.runs()
          .filter((r) => !!r.environment)
          .map((r) => (isBranchEnvironment(r.source) ? branchTypeFilterValue(r.environment!) : r.environment!)),
        ...(active ? [active] : []),
      ]),
    ].sort();
  });

  /** Bearbeiter kommen aus der Projekt-Mitgliederliste, nicht aus der geladenen Seite. */
  readonly assigneeOptions = computed(() =>
    this.projectAssignees()
      .map((a) => [a.id, a.name] as const)
      .sort((a, b) => a[1].localeCompare(b[1])),
  );

  // Als Trigger fuer Neuberechnung der uebersetzten Dropdown-Options und Stats beim
  // Sprachwechsel. Wird als nackte Anweisung gerufen — nur der Signal-Read zaehlt.
  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });

  readonly artDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.langChange();
    return [
      { value: 'MANUAL', label: this.translate.instant('testRun.art.MANUAL') },
      { value: 'AUTOMATED', label: this.translate.instant('testRun.art.AUTOMATED') },
    ];
  });

  readonly statusDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.langChange();
    return [
      { value: 'PASSED', label: this.translate.instant('testResults.result.passed') },
      { value: 'FAILED', label: this.translate.instant('testResults.result.failed') },
      { value: 'RUNNING', label: this.translate.instant('testResults.result.running') },
      { value: 'BLOCKED', label: this.translate.instant('testResults.result.blocked') },
      { value: 'SKIPPED', label: this.translate.instant('testResults.result.skipped') },
      { value: 'NOT_RUN', label: this.translate.instant('testResults.result.not_run') },
      { value: ABORTED_OPTION, label: this.translate.instant('testRuns.status.aborted') },
    ];
  });

  readonly environmentDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.environmentOptions().map((v) => ({ value: v, label: environmentFilterLabel(v) })),
  );

  readonly assigneeDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.assigneeOptions().map(([id, name]) => ({ value: id, label: name })),
  );

  /**
   * Der Bearbeiter eines Pipeline-Laufs (NEX-1332). Haengt weder an der Zeile noch am
   * Lauf, sondern allein an der Sprache — deshalb hier einmal statt in jeder Zeile bei
   * jedem Change-Detection-Lauf, und ueber `langChange` wie die uebrigen uebersetzten
   * Beschriftungen dieser Klasse.
   */
  private readonly pipelineAssignee = computed(() => {
    this.langChange();
    return this.translate.instant('testRuns.pipelineAssignee') as string;
  });

  /**
   * Alle vier Zahlen stammen aus demselben Aufruf und damit aus derselben
   * Grundmenge (NEX-1081). Vorher las GESAMT die Server-Gesamtzahl, die anderen
   * drei zaehlten nur die geladenen Zeilen - beim Blaettern widersprachen sie sich.
   */
  readonly stats = computed<StatItem[]>(() => {
    this.langChange();
    const counts = this.statsData();
    // Bei Stats-Fehler bewusst „—" statt 0: die Kacheln sind sonst nicht mehr von
    // einer echten Nullmenge zu unterscheiden — Nutzer laesen dann GESAMT=0 neben
    // 25 sichtbaren Zeilen und haetten keinen Anhalt, dass die Zahlen fehlen.
    const fallback: string | number = this.statsError() ? '—' : 0;
    return [
      { label: this.translate.instant('testRuns.stats.total'), value: counts?.total ?? fallback },
      { label: this.translate.instant('testRuns.stats.passed'), value: counts?.passed ?? fallback, color: '#15803D' },
      { label: this.translate.instant('testRuns.stats.failed'), value: counts?.failed ?? fallback, color: '#B91C1C' },
      { label: this.translate.instant('testRuns.stats.running'), value: counts?.running ?? fallback, color: '#3B82F6' },
    ];
  });

  /**
   * Serverseitiges Blaettern ohne Ladezustand: der Nutzer kann waehrend eines
   * laufenden Requests weiterklicken. Mit `subscribe` je Aufruf ueberschriebe eine
   * spaeter eintreffende, aeltere Antwort die neuere und die Tabelle zeigte die
   * falsche Seite. `switchMap` bricht den ueberholten Request ab.
   */
  private readonly reload = new Subject<void>();
  /**
   * Eigener Kanal fuer die Sucheingabe. Dropdown-Wechsel bleiben unmittelbar
   * (`applyFilterChange` → `reload.next()`), damit die Anzeige direkt reagiert.
   * Tippen dagegen laeuft durch `debounceTime` und feuert nach der Ruhephase genau
   * einen Reload (NEX-1081-Review MZemelkaST).
   */
  private readonly queryReload = new Subject<void>();

  constructor() {
    this.projectId = this.findParam('projectId') ?? '';
    this.subscribeToLoads();
    if (this.projectId) {
      this.liveRunsStore.watch(this.projectId);
    }
    this.load();
    this.loadSavedViews();
    this.loadAssignees();
  }

  private subscribeToLoads(): void {
    this.queryReload
      .pipe(debounceTime(QUERY_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reload.next());

    // Das Ende eines Laufs aendert Status, Dauer und Kennzahlen, die nur der Server kennt.
    // Neu geladen wird die aktuelle Seite: Filter, Suche und Seite bleiben stehen.
    this.liveRunsStore.ended
      .pipe(
        tap((runId) => this.endedRunIds.update((ids) => new Set(ids).add(runId))),
        debounceTime(RUN_END_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.load());

    this.reload
      .pipe(
        switchMap(() =>
          this.testRunService
            .listPaged(this.projectId, this.currentFilter(), this.pageIndex(), this.pageSize())
            .pipe(catchError(() => of(LOAD_FAILED))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => this.applyPage(result));

    // Stats-Fehler wird ausdruecklich vom Listen-Fehler getrennt: waehrend die
    // Liste weiter Zeilen zeigt, blieben die Kacheln sonst stumm bei „0" stehen.
    this.reload
      .pipe(
        switchMap(() =>
          this.testRunService.getStats(this.projectId, this.currentFilter()).pipe(
            catchError(() => {
              this.statsError.set(true);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result !== null) this.statsError.set(false);
        this.statsData.set(result);
      });
  }

  /**
   * Im Fehlerfall wird die Liste geleert. Vorher blieben die alten Zeilen stehen -
   * mit funktionierendem Oeffnen- und Loeschen-Knopf auf Daten, von denen niemand
   * mehr weiss, ob es sie noch gibt (NEX-1081).
   */
  private applyPage(result: Page<TestRunListItem> | typeof LOAD_FAILED): void {
    this.loading.set(false);
    this.endedRunIds.set(new Set());
    if (result === LOAD_FAILED) {
      this.runs.set([]);
      this.totalElements.set(0);
      this.loadError.set(true);
      return;
    }
    // Eine Antwort ohne `content` wuerde das Signal auf undefined setzen und
    // jede Auswertung der Liste zum Absturz bringen (NEX-1034).
    const content = result.content ?? [];
    this.runs.set(content);
    this.totalElements.set(result.totalElements);

    // Nach dem Loeschen der letzten Zeile einer hinteren Seite gibt es diese Seite
    // nicht mehr, und der Nutzer saehe eine leere Tabelle trotz vorhandener Treffer.
    // Das Bedienelement kann das nicht abfangen: es erfaehrt den neuen Gesamtstand
    // erst mit dieser Antwort (NEX-1081).
    if (content.length === 0 && result.totalElements > 0 && this.pageIndex() > 0) {
      const lastPage = Math.max(0, Math.ceil(result.totalElements / this.pageSize()) - 1);
      // Nur zurueckspringen, nie erneut dieselbe Seite anfordern. Meldet der Server
      // eine Gesamtzahl, die zu seiner eigenen leeren Seite nicht passt, waere ein
      // Nachladen auf denselben Index eine endlose Schleife gegen das Backend.
      if (lastPage < this.pageIndex()) {
        this.pageIndex.set(lastPage);
        this.load();
      }
    }
  }

  private loadAssignees(): void {
    if (!this.projectId) return;
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (members) =>
          this.projectAssignees.set(
            members
              .filter((m) => m.tenantUserId)
              .map((m) => ({
                id: m.tenantUserId as string,
                name: `${m.firstName} ${m.lastName}`.trim() || m.email,
              })),
          ),
        error: () => this.projectAssignees.set([]),
      });
  }

  load(): void {
    if (!this.beginLoad()) return;
    this.reload.next();
  }

  /**
   * Setzt die drei Lade-Flags zurueck und meldet, ob geladen werden darf. Ohne
   * Projekt-ID steigt der Aufruf still aus — der Guard darf nicht per Copy-Paste
   * in `onQueryInput` verloren gehen, sonst tippt der Nutzer gegen
   * `/api/v1/projects//test-runs` (NEX-1081-Review).
   */
  private beginLoad(): boolean {
    if (!this.projectId) return false;
    this.loading.set(true);
    this.loadError.set(false);
    this.statsError.set(false);
    return true;
  }

  onPageIndexChange(index: number): void {
    this.pageIndex.set(index);
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
    this.load();
  }

  /**
   * Jeder Filterwechsel springt auf Seite 1 zurueck. Sonst bliebe man nach dem
   * Filtern auf Seite 3 und haelt das leere Ergebnis fuer "nichts gefunden"
   * (NEX-1081).
   */
  private applyFilterChange(): void {
    this.pageIndex.set(0);
    this.load();
  }

  private currentFilter(): TestRunListFilter {
    return {
      query: this.query(),
      art: this.artFilter(),
      status: this.statusFilter(),
      environment: this.environmentFilter(),
      assignee: this.assigneeFilter(),
      runStatus: this.runStatusFilter(),
    };
  }

  onQueryInput(value: string): void {
    // Signale bleiben unmittelbar (Filter-Chip, Reset-Button reagieren sofort),
    // nur der Netzwerk-Aufruf laeuft ueber den `queryReload`-Debounce.
    this.query.set(value);
    this.pageIndex.set(0);
    if (!this.beginLoad()) return;
    this.queryReload.next();
  }

  onArtChange(value: string): void {
    this.artFilter.set(value as RunKind | '');
    this.applyFilterChange();
  }

  onStatusChange(value: string): void {
    const aborted = value === ABORTED_OPTION;
    this.statusFilter.set(aborted ? '' : (value as ResultStatus | ''));
    this.runStatusFilter.set(aborted ? ABORTED_OPTION : '');
    this.applyFilterChange();
  }

  onEnvironmentChange(value: string): void {
    this.environmentFilter.set(value);
    this.applyFilterChange();
  }

  onAssigneeChange(value: string): void {
    this.assigneeFilter.set(value);
    this.applyFilterChange();
  }

  resetFilters(): void {
    this.query.set('');
    this.artFilter.set('');
    this.statusFilter.set('');
    this.runStatusFilter.set('');
    this.environmentFilter.set('');
    this.assigneeFilter.set('');
    this.applyFilterChange();
  }

  /**
   * Fuehrt auf die Durchlaeufe-Zwischenebene, nicht direkt in den Runner
   * (NEX-1037): ein Lauf kann mehrfach durchgefuehrt worden sein, und welchen
   * Durchlauf der Nutzer sehen will, entscheidet er dort.
   */
  openRun(run: TestRunListItem): void {
    const link = testRunExecutionsLink(this.projectId, run.id, run.art);
    this.router.navigate(link.commands, { queryParams: link.queryParams });
  }

  createRun(): void {
    this.router.navigate(['/projects', this.projectId, 'test-runs', 'new']);
  }

  askDelete(run: TestRunListItem): void {
    this.deleteError.set(false);
    this.pendingDelete.set(run);
  }

  cancelDelete(): void {
    this.pendingDelete.set(null);
  }

  confirmDelete(): void {
    const run = this.pendingDelete();
    if (!run) return;
    this.deleting.set(true);
    this.testRunService
      .deleteRun(this.projectId, run.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deleting.set(false);
          this.pendingDelete.set(null);
          this.load();
        },
        error: () => {
          this.deleting.set(false);
          this.pendingDelete.set(null);
          this.deleteError.set(true);
        },
      });
  }

  /** Ohne Rechte-Check: ob der Nutzer abbrechen darf, entscheidet das Backend (403 im Dialog). */
  canAbort(run: TestRunListItem, live: LivePipelineEntry | undefined): boolean {
    return !live && !this.endedRunIds().has(run.id) && ABORTABLE_STATUSES.includes(run.runStatus);
  }

  openAbort(run: TestRunListItem): void {
    this.abortErrorKey.set(null);
    this.reloadAfterAbortDialog = false;
    this.pendingAbort.set(run);
  }

  closeAbort(): void {
    if (this.abortSubmitting()) return;
    this.pendingAbort.set(null);
    if (this.reloadAfterAbortDialog) {
      this.reloadAfterAbortDialog = false;
      this.load();
    }
  }

  /**
   * Ablauf wie im Runner: der Dialog bleibt bis zur Antwort stehen, eine Absage erscheint
   * in ihm. Status und Begruendung der Zeile holt das Neuladen — die Antwort ist ein
   * `TestRun`, keine Listenzeile.
   */
  submitAbort(reason: string): void {
    const run = this.pendingAbort();
    if (!run || this.abortSubmitting()) return;
    this.abortSubmitting.set(true);
    this.abortErrorKey.set(null);
    this.testRunService
      .abort(this.projectId, run.id, reason)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.abortSubmitting.set(false);
          this.pendingAbort.set(null);
          this.load();
        },
        error: (error: HttpErrorResponse) => {
          this.abortSubmitting.set(false);
          this.abortErrorKey.set(abortErrorKeyFor(error.status));
          if (error.status === 409) this.reloadAfterAbortDialog = true;
        },
      });
  }

  isAborted(run: TestRunListItem): boolean {
    return run.runStatus === 'ABORTED';
  }

  /** Aeltere Abbrueche tragen keine Begruendung; `null` laesst das Template den Ersatztext zeigen. */
  abortReason(run: TestRunListItem): string | null {
    return run.abortReason?.trim() || null;
  }

  /**
   * Der Fortschritt einer Zeile. Laeuft eine Pipeline zu ihr, zaehlen deren
   * erledigte Testfaelle statt der Schritte des Laufs — die Zeile zeigt dann
   * denselben Stand wie das Band darunter (NEX-1233). Ohne Schritte bleibt der
   * Balken leer.
   *
   * <p>Gezaehlt wird erledigt gegen gesamt, nicht bestanden gegen gesamt: so setzt es
   * die Design-Referenz (`page-testausfuehrung.jsx:194-197`) ein, und so passt der
   * Balken zu „offen = gesamt − erledigt" im Band. Ein Fehlschlag faerbt ihn deshalb
   * ueber den eigenen Zaehler, nicht ueber den Endstand des Laufs — sonst bliebe er
   * blau, waehrend das Band darunter schon die rote Eins zeigt.
   */
  runProgress(run: TestRunListItem, live: LivePipelineEntry | undefined): RunProgress {
    const counts = live ? pipelineCounts(live) : null;
    const passed = counts ? counts.done : run.stepsPassed;
    const total = counts ? counts.total : run.stepsTotal;
    return {
      passed,
      total,
      label: `${passed}/${total}`,
      percent: total > 0 ? Math.round((passed / total) * 100) : 0,
      // Ohne Faelle gibt es nichts einzufaerben. Ein Pipeline-Lauf ohne Testreport ist
      // rot ausgegangen und traegt trotzdem keinen roten Balken — der Balken beschreibt
      // die Faelle, und die gibt es hier nicht; sein Ergebnis steht in der Status-Spalte
      // (NEX-1332).
      failed: total > 0 && (counts ? counts.failed > 0 : run.status === 'FAILED'),
      labelKey: live ? 'testRuns.columns.cases' : 'testRuns.columns.steps',
    };
  }

  /**
   * Wer den Lauf verantwortet (NEX-1332). Ein Lauf aus der Pipeline hat keine Person, aber
   * auch keinen Strich: er gehoert der CI, und der Entwurf fuehrt ihn wie jede andere
   * Bearbeiterin. Eine zugewiesene Person gewinnt trotzdem — wer einen Pipeline-Lauf
   * uebernommen hat, steht dort selbst.
   */
  assigneeLabel(run: TestRunListItem): string | null {
    return run.assignee ?? (run.source === 'PIPELINE' ? this.pipelineAssignee() : null);
  }

  statusKey(run: TestRunListItem): string {
    return (run.status ?? 'NOT_RUN').toLowerCase();
  }

  resPillStatus(run: TestRunListItem): ResPillStatus {
    return TEST_RUN_TO_RES_PILL[run.status ?? 'NOT_RUN'];
  }

  duration(run: TestRunListItem): string {
    return formatDurationMs(run.durationMs);
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViewData().find((v) => v.name === name);
    if (!view?.filtersJson) return;
    const f = parseSavedViewFilters(view.filtersJson, EMPTY_TEST_RUN_VIEW_FILTERS);
    this.query.set(f.query);
    this.artFilter.set(f.art);
    // Ansichten von vor NEX-1400 tragen keinen runStatus. Das Dropdown bietet nur
    // „Abgebrochen" an und zeigt hoechstens einen der beiden Filter; eine von Hand
    // geschriebene Ansicht mit beiden oder einem anderen Wert soll nicht mehr filtern,
    // als die Auswahl zeigt.
    const runStatus = f.runStatus === ABORTED_OPTION ? ABORTED_OPTION : '';
    this.runStatusFilter.set(runStatus);
    this.statusFilter.set(runStatus ? '' : f.status);
    this.environmentFilter.set(f.environment);
    this.assigneeFilter.set(f.assignee);
    this.applyFilterChange();
  }

  onSavedViewSaved(name: string): void {
    this.savedViewService
      .upsert(this.projectId, {
        name,
        context: SAVED_VIEW_CONTEXT,
        filtersJson: buildSavedViewFiltersJson(this.currentFilter()),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadSavedViews() });
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewService
      .delete(this.projectId, SAVED_VIEW_CONTEXT, name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.loadSavedViews() });
  }

  private loadSavedViews(): void {
    if (!this.projectId) return;
    this.savedViewService
      .list(this.projectId, SAVED_VIEW_CONTEXT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViewData.set(views),
        error: () => this.savedViewData.set([]),
      });
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
